import { useZipCodeInput } from '@/components/content/Header/parts/DeliveryLocator/hooks/useZipCodeInput';
import {
	geoLocRequestBtnSX,
	geoLocRequestPrivacyLinkSX,
	geoLocRequestViewContainerSX,
	geoLocRequestViewLinkSX,
	geoLocRequestViewListItemSX,
	geoLocRequestViewListSX,
} from '@/components/content/Header/parts/DeliveryLocator/styles/GeolocalizationRequestView/geolocalizationRequestViewStyles';
import DynamicIcon from '@/components/icons/DynamicIcon';
import { ContentContext } from '@/data/context/content';
import { useLocalization } from '@/data/Localization';
import {
	Box,
	Button,
	CircularProgress,
	Link,
	List,
	ListItem,
	Stack,
	Typography,
} from '@mui/material';
import { Dispatch, ReactNode, SetStateAction, useContext } from 'react';
import { MATERIAL_SYMBOL_ICONS } from 'shared/custom/constants/commonConstants';

type Props = {
	setOpenGeolocalizationRequestView: Dispatch<SetStateAction<boolean>>;
};

const BulletListItem = ({ children }: { children: ReactNode }) => (
	<ListItem sx={geoLocRequestViewListItemSX}>
		<Typography variant="bodySubText">{children}</Typography>
	</ListItem>
);

export const GeolocalizationRequestView = ({ setOpenGeolocalizationRequestView }: Props) => {
	const localization = useLocalization('DeliveryLocator');

	const handleBack = () => {
		setOpenGeolocalizationRequestView(false);
	};

	const { loading, handleUseCurrentLocation } = useContext(ContentContext) as ReturnType<
		typeof useZipCodeInput
	>;

	return (
		<Stack gap={1.25}>
			<Link display="flex" alignItems="center" sx={geoLocRequestViewLinkSX} onClick={handleBack}>
				<DynamicIcon name={MATERIAL_SYMBOL_ICONS.keyboardArrowLeft} />
				<Typography variant="bodySubText">{localization.goBack.t()}</Typography>
			</Link>
			<Box sx={geoLocRequestViewContainerSX}>
				<Stack direction={'column'} gap={'15px'}>
					<Typography variant="bodySubText">
						{localization.GeolocalizationRequestView.content1.t()}
					</Typography>
					<List sx={geoLocRequestViewListSX}>
						<BulletListItem>{localization.GeolocalizationRequestView.content2.t()}</BulletListItem>
						<BulletListItem>{localization.GeolocalizationRequestView.content3.t()}</BulletListItem>
						<BulletListItem>{localization.GeolocalizationRequestView.content4.t()}</BulletListItem>
						<BulletListItem>{localization.GeolocalizationRequestView.content5.t()}</BulletListItem>
					</List>
					<Button variant="outlined" fullWidth sx={geoLocRequestBtnSX} onClick={handleBack}>
						<Typography variant="bodyBaseline">
							{localization.GeolocalizationRequestView.chooseManually.t()}
						</Typography>
					</Button>
				</Stack>
				<Button
					variant="contained"
					fullWidth
					sx={{ ...geoLocRequestBtnSX, mb: '20px' }}
					onClick={handleUseCurrentLocation}
					startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
				>
					{loading ? null : (
						<Typography variant="bodyBaseline">
							{localization.GeolocalizationRequestView.useMyLocation.t()}
						</Typography>
					)}
				</Button>
			</Box>
			<Link
				sx={geoLocRequestPrivacyLinkSX}
				href={localization.GeolocalizationRequestView.locationPrivacyURL.t()}
				target="_blank"
				rel="noreferrer"
			>
				<Typography variant="bodySubText">
					{localization.GeolocalizationRequestView.locationPrivacy.t()}
				</Typography>
			</Link>
		</Stack>
	);
};
